import React, { useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { adminAPI } from '../../services/api';
import PhoneInput from '../common/PhoneInput';

const CreateUser = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [userRole, setUserRole] = useState('agent');

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    ghanaCard: '',
    email: '',
    address: '',
    assembly: 'Accra Metro',
    treatmentCategory: 'TREATMENT_1',
  });

  const handleSubmit = async () => {
    setError('');
    if (!formData.firstName?.trim() || !formData.lastName?.trim() || !formData.phone?.trim()) {
      setError('First name, last name, and phone are required');
      return;
    }

    if (!formData.email?.trim()) {
      setError('Email is required');
      return;
    }

    if (userRole === 'agent') {
      const agentFields = ['ghanaCard', 'email', 'address', 'assembly', 'treatmentCategory'];
      if (agentFields.some((k) => !formData[k]?.trim?.())) {
        setError('Please fill all fields for an agent (including assembly and treatment)');
        return;
      }
    }

    setLoading(true);
    try {
      let result;
      if (userRole === 'supervisor') {
        result = await adminAPI.createSupervisor({
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          email: formData.email.trim(),
          phone: formData.phone?.trim() || undefined,
          ghanaCard: formData.ghanaCard?.trim() || undefined,
          address: formData.address?.trim() || undefined,
        });
        toast.success('Supervisor created successfully');
      } else if (userRole === 'external') {
        result = await adminAPI.createExternalUser({
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          ghanaCard: formData.ghanaCard?.trim() || undefined,
          address: formData.address?.trim() || undefined,
        });
        toast.success('External user created successfully');
      } else {
        result = await adminAPI.createAgent({
          ...formData,
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
        });
        toast.success('Agent created successfully');
      }

      setTimeout(() => navigate('/admin/dashboard'), 1000);
    } catch (err) {
      let msg = err.response?.data?.message || `Failed to create ${userRole}`;
      if (err.response?.status === 409) {
        msg = 'Email or phone already registered';
      }
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const roleHint =
    userRole === 'supervisor'
      ? 'Supervisors now log in with email/phone + password.'
      : userRole === 'external'
        ? 'External users now log in with email/phone + password.'
        : 'Agents need assembly and treatment category for billing and permissions.';

  const optionalLabel = userRole !== 'agent';
  const emailPlaceholder =
    userRole === 'supervisor' ? 'supervisor@example.com' : userRole === 'external' ? 'viewer@partner.org' : 'agent@email.com';

  return (
    <div className='h-full flex flex-col'>
      <header className='sticky top-0 z-30 p-6 bg-[#001E3C] flex items-center gap-4 border-b border-white/10'>
        <button
          type='button'
          onClick={() => navigate('/admin/dashboard')}
          className='glass-card p-3 rounded-xl active:scale-95 transition'>
          <ChevronLeft className='w-5 h-5 text-white' />
        </button>
        <h2 className='text-xl font-black uppercase text-white'>Create User</h2>
      </header>

      <div className='flex-1 overflow-y-auto no-scrollbar p-6 space-y-6 pb-20'>
        <div className='space-y-2'>
          <label className='block text-sm font-bold text-white'>User type</label>
          <div className='grid grid-cols-3 gap-1 rounded-xl overflow-hidden border border-white/10'>
            <button type='button' onClick={() => setUserRole('agent')} className={`py-3 px-1 text-[11px] sm:text-sm font-black uppercase transition ${userRole === 'agent' ? 'bg-[#FFC107] text-black' : 'bg-white/5 text-white/80'}`}>Agent</button>
            <button type='button' onClick={() => setUserRole('supervisor')} className={`py-3 px-1 text-[11px] sm:text-sm font-black uppercase transition ${userRole === 'supervisor' ? 'bg-[#00BFA5] text-black' : 'bg-white/5 text-white/80'}`}>Supervisor</button>
            <button type='button' onClick={() => setUserRole('external')} className={`py-3 px-1 text-[10px] sm:text-xs font-black uppercase transition ${userRole === 'external' ? 'bg-[#9C27B0] text-white' : 'bg-white/5 text-white/80'}`}>External</button>
          </div>
          <p className='text-xs text-white/50'>{roleHint}</p>
        </div>

        <div className='space-y-4 text-white'>
          <div>
            <label className='block text-sm font-bold mb-2'>First Name</label>
            <input type='text' value={formData.firstName} onChange={(e) => setFormData({ ...formData, firstName: e.target.value })} className='w-full p-4 bg-white/5 border border-white/10 rounded-xl outline-none text-white' placeholder='First name' />
          </div>
          <div>
            <label className='block text-sm font-bold mb-2'>Last Name</label>
            <input type='text' value={formData.lastName} onChange={(e) => setFormData({ ...formData, lastName: e.target.value })} className='w-full p-4 bg-white/5 border border-white/10 rounded-xl outline-none text-white' placeholder='Last name' />
          </div>
          <div>
            <label className='block text-sm font-bold mb-2'>Phone</label>
            <PhoneInput value={formData.phone} onChange={(v) => setFormData({ ...formData, phone: v })} placeholder='XXXXXXXXX' className='w-full' />
          </div>
          <div>
            <label className='block text-sm font-bold mb-2'>Email</label>
            <input type='email' value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className='w-full p-4 bg-white/5 border border-white/10 rounded-xl outline-none text-white' placeholder={emailPlaceholder} />
          </div>
          <div>
            <label className='block text-sm font-bold mb-2'>Ghana Card Number{optionalLabel ? ' (optional)' : ''}</label>
            <input type='text' value={formData.ghanaCard} onChange={(e) => setFormData({ ...formData, ghanaCard: e.target.value })} className='w-full p-4 bg-white/5 border border-white/10 rounded-xl outline-none text-white' placeholder='GHA-XXXXXXXXX' />
          </div>
          <div>
            <label className='block text-sm font-bold mb-2'>Address{optionalLabel ? ' (optional)' : ''}</label>
            <input type='text' value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} className='w-full p-4 bg-white/5 border border-white/10 rounded-xl outline-none text-white' placeholder='Address' />
          </div>

          {userRole === 'agent' && (
            <>
              <div>
                <label className='block text-sm font-bold mb-2'>Assembly</label>
                <select value={formData.assembly} onChange={(e) => setFormData({ ...formData, assembly: e.target.value })} className='w-full p-4 bg-white/5 border border-white/10 rounded-xl outline-none text-white'>
                  <option value='Accra Metro' className='bg-[#001E3C] text-white'>Ablekuma North Municipal Assembly</option>
                  <option value='Lower Manya Krobo Municipal Assembly' className='bg-[#001E3C] text-white'>Lower Manya Krobo Municipal Assembly</option>
                  <option value='Yilo Krobo Municipal Assembly' className='bg-[#001E3C] text-white'>Yilo Krobo Municipal Assembly</option>
                  <option value='Nsawam Adoagyiri Municipal Assembly' className='bg-[#001E3C] text-white'>Nsawam Adoagyiri Municipal Assembly</option>
                </select>
              </div>
              <div>
                <label className='block text-sm font-bold mb-2'>Treatment Category</label>
                <select value={formData.treatmentCategory} onChange={(e) => setFormData({ ...formData, treatmentCategory: e.target.value })} className='w-full p-4 bg-white/5 border border-white/10 rounded-xl outline-none text-white'>
                  <option value='TREATMENT_1' className='bg-[#001E3C] text-white'>Treatment 1 (Limited Info)</option>
                  <option value='TREATMENT_2' className='bg-[#001E3C] text-white'>Treatment 2 (Full Info)</option>
                </select>
              </div>
            </>
          )}
        </div>

        {error && <div className='p-3 bg-red-500/20 border border-red-500/50 rounded-xl text-red-300 text-sm text-center'>{error}</div>}

        <button type='button' onClick={handleSubmit} disabled={loading} className='w-full py-4 rounded-2xl btn-gold font-black uppercase text-sm mt-8 disabled:opacity-50'>
          {loading ? 'Creating...' : 'Create user'}
        </button>
      </div>
    </div>
  );
};

export default CreateUser;
